import { z } from "zod";

// Online service requests (the public intake form). Few statuses, one direction:
// new → quoted (draft estimate exists) → scheduled (job assigned) → closed.
export const RequestStatusSchema = z.enum(["new", "quoted", "scheduled", "closed"]);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

export const RequestKindSchema = z.enum(["estimate", "work_order"]);
export type RequestKind = z.infer<typeof RequestKindSchema>;

export const ServiceRequestSchema = z.object({
  kind: RequestKindSchema,
  name: z.string().min(1).max(120),
  email: z.string().max(200).default(""),
  phone: z.string().max(40).default(""),
  address: z.string().max(300).default(""),
  details: z.string().min(1).max(2000),
  preferredTimes: z.string().max(300).default(""),
  status: RequestStatusSchema.default("new"),
  /** "web" = the Clevis page, "website" = the company's own site (embedded or its own form). */
  source: z.string().max(40).default("web"),
  /** The company's own service types ("Garage door repair") and the department they route to. */
  serviceType: z.string().max(80).default(""),
  departmentId: z.string().nullable().default(null),
  /** Where the lead came from, for ROI: what they said, and the campaign tags on the link. */
  lead: z
    .object({
      howHeard: z.string().max(80).default(""),
      utmSource: z.string().max(100).default(""),
      utmMedium: z.string().max(100).default(""),
      utmCampaign: z.string().max(100).default(""),
      /** The website page the form was on. */
      page: z.string().max(300).default(""),
    })
    .default({}),
  // Set as the request moves through the pipeline.
  customerId: z.string().nullable().default(null),
  estimateId: z.string().nullable().default(null),
  jobId: z.string().nullable().default(null),
  handledByUid: z.string().nullable().default(null),
  /** Booked by the website chat: the day and window they picked, and what was said. */
  chat: z
    .object({
      date: z.string(),
      window: z.string(),
      transcript: z.array(z.object({ role: z.enum(["user", "assistant"]), text: z.string().max(4000) })).max(80),
    })
    .optional(),
  /** Clevis Relay: came through a customer's referral link. */
  referral: z.object({ referralId: z.string(), code: z.string(), customerId: z.string(), name: z.string().default("") }).optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type ServiceRequest = z.infer<typeof ServiceRequestSchema>;

// ---------- AI chat scheduling (Requests → AI chat scheduling) ----------
// A chat bubble on the company's website that books visits. Everything it
// offers is the company's: its services, its arrival windows, its days, how
// many bookings a day, its words. Every booking lands in Requests, pending,
// until the office approves — the chat never touches the calendar.

export const ChatWindowSchema = z.object({
  label: z.string().trim().min(1).max(40),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
});
export type ChatWindow = z.infer<typeof ChatWindowSchema>;
export const DEFAULT_CHAT_WINDOWS: ChatWindow[] = [
  { label: "8-10 AM", start: "08:00", end: "10:00" },
  { label: "10 AM-12", start: "10:00", end: "12:00" },
  { label: "1-3 PM", start: "13:00", end: "15:00" },
  { label: "3-5 PM", start: "15:00", end: "17:00" },
];

export const AiChatSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** What the bubble calls itself ("Ask CSG"); blank = "<company>'s assistant". */
  assistantName: z.string().max(40).default(""),
  /** The first line visitors see; blank = a sensible default. */
  greeting: z.string().max(500).default(""),
  /** Website-form service types it may book (ids); empty = all of them. */
  serviceTypeIds: z.array(z.string()).max(20).default([]),
  /** Arrival windows it may offer, company time. */
  windows: z.array(ChatWindowSchema).min(1).max(8).default(DEFAULT_CHAT_WINDOWS),
  /** Days it may book: 0 = Sunday … 6 = Saturday. */
  days: z.array(z.number().int().min(0).max(6)).default([1, 2, 3, 4, 5]),
  /** Earliest day it may offer: 0 = today, 1 = tomorrow. */
  leadDays: z.number().int().min(0).max(30).default(1),
  /** How far out it may book. */
  horizonDays: z.number().int().min(1).max(60).default(14),
  /** Visits a day (on the calendar or waiting for approval) before a day is full; 0 = no limit. */
  maxPerDay: z.number().int().min(0).max(100).default(6),
  askAddress: z.boolean().default(true),
  /** The company's own guidance, in its words ("We don't service commercial doors."). */
  instructions: z.string().max(2000).default(""),
  /** The bubble on their site. */
  buttonLabel: z.string().max(30).default("Chat with us"),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#DF8048"),
  updatedAt: z.number().default(0),
});
export type AiChatSettings = z.infer<typeof AiChatSettingsSchema>;

// The public form's setup (Requests → Website form). Read by members, written by functions.
export const RequestServiceTypeSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string().trim().min(1).max(80),
  departmentId: z.string().nullable().default(null),
});
export type RequestServiceType = z.infer<typeof RequestServiceTypeSchema>;

export const DEFAULT_HOW_HEARD = ["Google search", "Friend or neighbor", "Our truck or yard sign", "Manufacturer website", "Social media", "Other"];

export const RequestFormSettingsSchema = z.object({
  /** What people pick as "Type of service"; none = the form asks estimate vs. service call only. */
  serviceTypes: z.array(RequestServiceTypeSchema).max(20).default([]),
  askHowHeard: z.boolean().default(true),
  howHeardOptions: z.array(z.string().trim().min(1).max(80)).max(20).default(DEFAULT_HOW_HEARD),
  /** The company's own website(s), e.g. https://csglv.com: may send requests from their own form. */
  websiteOrigins: z.array(z.string().max(200)).max(10).default([]),
  updatedAt: z.number().default(0),
});
export type RequestFormSettings = z.infer<typeof RequestFormSettingsSchema>;
